import { beep, sleep } from "@/lib/clickTone";
import { detectOnsets } from "@/lib/audioAnalysis";
import { startRecordingWithTimeout } from "@/lib/micHelp";

const CLICKS = [0.6, 1.2, 1.8, 2.4, 3.0];

/**
 * Sync calibration — plays 5 clicks through the speakers / headphones while recording the mic through the exact same
 * path a take uses, then measures how late each click lands. → round-trip latency in ms.
 */
export async function calibrateLatency(engine, deviceId) {
  const idx = engine.channels.findIndex((c) => !c.buffer && !c.recOwner);
  if (idx < 0) throw new Error("All 64 studio channels are full — clear one in the Advanced Studio first.");
  const prev = { autoTrim: engine.autoTrimSilence, mode: engine.recordingMode };
  engine.pause();
  Object.assign(engine, { autoTrimSilence: false, recordingMode: "dry" });
  try {
    await startRecordingWithTimeout(engine, idx, { deviceId: deviceId || null });
    const tRec = engine.ctx.currentTime;
    CLICKS.forEach((d) => beep(engine.ctx, tRec + d, { freq: 1800, dur: 0.03, gain: 0.9 }));
    await sleep(3700);
    const res = await engine.stopRecording();
    if (!res?.buffer) throw new Error("We couldn't hear the clicks. Hold your headphones right against the mic (or turn speakers on low) and try again.");
    const onsets = detectOnsets(res.buffer);
    const lags = CLICKS.map((d) => onsets.find((o) => o >= d - 0.02 && o <= d + 0.6)).map((o, i) => (o == null ? null : o - CLICKS[i])).filter((v) => v != null).sort((a, b) => a - b);
    if (lags.length < 3) throw new Error("We only heard some of the clicks. Hold the headphones closer to the mic, or turn the volume up a little.");
    if (lags[lags.length - 1] - lags[0] > 0.035) throw new Error("The clicks were uneven — try again in a quieter moment.");
    return Math.round(lags[Math.floor(lags.length / 2)] * 1000);
  } finally {
    engine.clearChannel(idx);
    Object.assign(engine, { autoTrimSilence: prev.autoTrim, recordingMode: prev.mode });
  }
}
